import React from 'react';
import { DashboardIcons } from '../../../../../icons/icons';

import { ApplicantDocument } from '../../../../../utils/types/JobOpenings';

interface DocumentsTabProps {
  documents: ApplicantDocument[];
  selectedDocumentId?: string;
  onDocumentSelect: (doc: ApplicantDocument) => void;
  // Fallback for resume if documents is empty
  pdfUrl?: string;
  baseFilename?: string;
  uploadedAt?: number | string;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  selectedDocumentId,
  onDocumentSelect,
  pdfUrl,
  baseFilename,
  uploadedAt,
}) => {
  // Synthesize a resume document if documents is empty and pdfUrl exists
  const effectiveDocuments =
    (!documents || documents.length === 0) && pdfUrl
      ? [
        {
          id: 'resume-fallback',
          jobApplicantId: '',
          jobOpeningId: '',
          requirementKey: 'resume',
          requirementLabel: 'Resume',
          s3Filename: baseFilename || 'Resume.pdf',
          baseFilename: baseFilename || 'Resume.pdf',
          fileHash: '',
          uploadedAt: typeof uploadedAt === 'number' ? uploadedAt : 0,
        } as ApplicantDocument,
      ]
      : documents || [];

  // Group documents by requirementLabel
  const groups = effectiveDocuments.reduce(
    (acc: { [key: string]: ApplicantDocument[] }, doc) => {
      const groupName = doc.requirementLabel || 'Other';
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(doc);
      return acc;
    },
    {},
  );

  const sortedGroupNames = Object.keys(groups).sort((a, b) => {
    if (a.toLowerCase() === 'resume') return -1;
    if (b.toLowerCase() === 'resume') return 1;
    return a.localeCompare(b);
  });

  if (effectiveDocuments.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center p-12 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200'>
        <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
          <svg
              className='w-8 h-8 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-semibold text-gray-900 mb-1'>
          No Documents Found
        </h3>
        <p className='text-gray-500 max-w-xs'>
          No documents have been uploaded for this candidate yet.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-8 p-1'>
      { sortedGroupNames.map((groupName, groupIdx) => (
        <div
            key={groupIdx}
            className='animate-slideUp'
            style={{ animationDelay: `${groupIdx * 0.1}s` }}
        >
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-base font-bold text-slate-800 flex items-center gap-2'>
              { groupName }
            </h3>
          </div>

          <div className='space-y-3'>
            { groups[groupName].map((doc) => (
              <div
                  key={doc.id}
                  onClick={() => onDocumentSelect(doc)}
                  className={`group relative bg-white border rounded-2xl p-4 cursor-pointer transition-all duration-300 ${
                    selectedDocumentId === doc.id || (!selectedDocumentId && doc.id === 'resume-fallback')
                      ? 'border-emerald-500 shadow-lg shadow-emerald-500/10 bg-emerald-50/30'
                      : 'border-slate-200 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/5'
                  }`}
              >
                <div className='flex items-center gap-4'>
                  { /* Icon Block */ }
                  <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                        doc.baseFilename?.toLowerCase().endsWith('.pdf')
                          ? 'bg-red-50 text-red-500'
                          : 'bg-blue-50 text-blue-500'
                      }`}
                  >
                    { doc.baseFilename?.toLowerCase().endsWith('.pdf') ? (
                      <svg
                          className='w-6 h-6'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                    ) : (
                      <svg
                          className='w-6 h-6'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z'
                        />
                      </svg>
                    ) }
                  </div>

                  { /* Content */ }
                  <div className='flex-1 min-w-0'>
                    <h4
                        className={`text-[13px] font-semibold truncate transition-colors ${
                          selectedDocumentId === doc.id || (!selectedDocumentId && doc.id === 'resume-fallback')
                            ? 'text-emerald-700'
                            : 'text-slate-700 group-hover:text-emerald-700'
                        }`}
                    >
                      { doc.baseFilename }
                    </h4>
                    <div className='flex items-center gap-2 mt-1'>
                      <span className='text-[11px] font-medium text-slate-400'>
                        Uploaded{ ' ' }
                        { new Date(doc.uploadedAt * 1000).toLocaleDateString(
                          'en-US',
                          { month: 'short', day: 'numeric', year: 'numeric' },
                        ) }
                      </span>
                    </div>
                  </div>

                  { /* Selected Indicator */ }
                  { (selectedDocumentId === doc.id || (!selectedDocumentId && doc.id === 'resume-fallback')) && (
                    <div className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse' />
                  ) }
                </div>
              </div>
            )) }
          </div>
        </div>
      )) }
    </div>
  );
};
